import axios, { Method } from "axios";
import Cookies from "js-cookie";

import { FeedbackMessage } from "../../components/BrieChatMenu/BrieChatFeedback";
import { UserRecordActivity } from "../../types/GlobalInterfaces";
import TenantAPI from "./TenantAPI";
import AuthAPI from "./AuthAPI";

export default class API {
  static async _unauthenticatedRequest(
    method: Method,
    url: string,
    data: any = null
  ) {
    return axios({
      method: method,
      url: window.GC_API_URL + url,
      data: data,
    });
  }

  static async _bearerRequest(
    method: Method,
    url: string,
    data: any = null,
    token = "",
    controller: any = null
  ) {
    const finalToken = token || "undefined";

    return axios({
      method: method,
      url: window.GC_API_URL + url,
      data: data,
      headers: {
        "app-id": "GrowthCloud",
        Authorization: "Bearer " + finalToken,
      },
      withCredentials: true,
      ...(controller && { signal: controller.signal }),
    });
  }

  /**
   * Sends a phone verification text.
   *
   * @param {*} tel
   */
  static async sendIdentityVerification(tel: string) {
    return this._unauthenticatedRequest("post", "/auth/identity/phone", {
      phone: tel,
    });
  }

  /**
   * Verifies the provided phone code and registers the user.
   */
  static async verifyIdentityAndRegister(
    registrationToken: string,
    identityToken: string,
    code: string,
    customClientID?: boolean, // Whether or not to pass client ID to the API. Example - During Emerge.ai Sign up.
    customAppID?: string
  ) {
    const clientID = await AuthAPI.getClientID().then(
      (data) => data.data.data.ClientID
    );

    return axios({
      method: "POST",
      url: `${window.GC_API_URL}/auth/identity/phone/verify`,
      headers: {
        "app-id": "GrowthCloud",
      },
      data: {
        registrationToken: registrationToken,
        identityToken: identityToken,
        verificationCode: code,
        customClientID: customClientID ? clientID : undefined,
        customAppID: customAppID,
      },
      withCredentials: true,
    });
  }

  // Attempts to refresh authentication token using session's refresh token
  static async refreshToken(tenantID: string | undefined) {
    return axios({
      method: "GET",
      headers: { "app-id": "GrowthCloud" },
      url:
        window.GC_API_URL +
        `/auth/token${tenantID ? `?tenantID=${tenantID}` : ""}`,
      withCredentials: true,
    });
  }

  // --------------------------------- Sessions ------------------------------------

  // Attempts to get all active sessions for a user.
  static async getSessions(count: number = 25, page: number = 1) {
    return this._bearerRequest(
      "put",
      "/auth/sessions",
      { count, page },
      Cookies.get("authenticationToken")
    );
  }

  /**
   * Attempts to get session history for a user
   * @param {string} userID - ID of the user we are attempting to get the session history for.
   */
  static async getSessionHistory(
    userID: string,
    count: number = 25,
    page: number = 1
  ) {
    return this._bearerRequest(
      "put",
      "/auth/sessions/history/" + userID,
      { count, page },
      Cookies.get("authenticationToken")
    );
  }

  /**
   * Attempts to remove a user's session
   */
  static async deleteSession(sessionID?: string) {
    return this._bearerRequest(
      "delete",
      "/auth/sessions/delete/" + sessionID,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getUser() {
    return this._bearerRequest(
      "get",
      "/account",
      null,
      Cookies.get("authenticationToken")
    );
  }

  /**
   * Attempts to authenticate a user using the local provider
   *
   * @param {*} id
   */
  static async getUserAccounts(id: string) {
    return this._bearerRequest(
      "post",
      "/auth/providers/local/getaccounts",
      {
        id: id,
      },
      Cookies.get("authenticationToken")
    );
  }

  static async updateUser(userID: string, payload: any = null) {
    return this._bearerRequest(
      "put",
      "/auth/providers/local/updateUser/" + userID,
      payload,
      Cookies.get("authenticationToken")
    );
  }

  /**
   * Attempts to register a user using the local provider
   *
   * @param {*} name
   * @param {*} email
   * @param {*} password
   */
  static async registerLocal(
    name: string,
    email: string,
    password: string,
    inviteToken?: string,
    appID?: string
  ) {
    return this._unauthenticatedRequest("post", "/auth/providers/local", {
      name: name,
      email: email,
      password: password,
      inviteToken: inviteToken,
      appID: appID,
    });
  }

  static async sendForgotPasswordEmail(email: string, redirectTo: string) {
    return this._unauthenticatedRequest(
      "put",
      `/auth/providers/local?redirectTo=${redirectTo}`,
      {
        email: email,
      }
    );
  }

  static async resetPassword(
    token: string,
    password: string,
    retypePassword: string
  ) {
    return this._unauthenticatedRequest("post", "/auth/providers/local/reset", {
      token,
      password,
      retypePassword,
    });
  }

  static async updateAccountPassword(
    accountID: string,
    oldPassword: string,
    newPassword: string
  ) {
    return this._bearerRequest(
      "put",
      "/auth/providers/local/updateAccountPassword/" + accountID,
      { oldPassword: oldPassword, newPassword: newPassword },
      Cookies.get("authenticationToken")
    );
  }

  static async deleteAccountCredential(accountID: string) {
    return this._bearerRequest(
      "delete",
      "/auth/providers/local/removeAccountCredential/" + accountID,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getAllModules() {
    return this._unauthenticatedRequest("get", "/modules");
  }

  static async getGlobalFieldTypes() {
    return this._bearerRequest(
      "get",
      "/global/crm/fieldtypes",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getGlobalCRMModules() {
    return this._bearerRequest(
      "get",
      "/global/crm/modules",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getGlobalCRMModuleByID(id = "") {
    return this._bearerRequest(
      "get",
      "/global/crm/modules/" + id,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async createGlobalCRMModule(
    name: string,
    apiName: string,
    protect = true
  ) {
    const payload = {
      Name: name,
      APIName: apiName,
      Protected: protect ? true : false,
    };

    return this._bearerRequest(
      "post",
      "/global/crm/modules",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteGlobalCRMModule(id: string) {
    return this._bearerRequest(
      "delete",
      "/global/crm/modules/" + id,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteGlobalCRMModuleField(id: string, apiName: string) {
    return this._bearerRequest(
      "delete",
      "/global/crm/modules/" + id + "/fields/" + apiName,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async createGlobalCRMModuleField(
    id: string,
    apiName: string,
    name: string,
    type: string,
    defaultValue = null,
    required = false,
    unique = false,
    protect = true,
    sources = ""
  ) {
    const payload = {
      Name: name,
      APIName: apiName,
      FieldType: type,
      DefaultValue: defaultValue ? defaultValue : null,
      Required: required ? true : false,
      Unique: unique ? true : false,
      Protected: protect ? true : false,
      SourcesList: sources || null,
    };

    return this._bearerRequest(
      "post",
      "/global/crm/modules/" + id + "/fields",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async editGlobalCRMModuleField(
    id: string,
    apiName: string,
    name: string,
    type: string,
    defaultValue = null,
    required = false,
    unique = false,
    protect = true,
    sources = ""
  ) {
    const payload = {
      Name: name,
      APIName: apiName,
      FieldType: type,
      DefaultValue: defaultValue,
      Required: required ? true : false,
      Unique: unique ? true : false,
      Protected: protect ? true : false,
      SourcesList: sources || null,
    };

    return this._bearerRequest(
      "put",
      "/global/crm/modules/" + id + "/fields/" + apiName,
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async getGlobalVariableConstants() {
    return this._bearerRequest(
      "get",
      "/global/variables/constants",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getGlobalVariableLists() {
    return this._bearerRequest(
      "get",
      "/global/variables/lists",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getGlobalVariableListByID(listName: string) {
    return this._bearerRequest(
      "get",
      "/global/variables/lists/" + listName,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async createGlobalVariableConstant(
    name: string,
    value: string | null
  ) {
    const payload = {
      Name: name,
      Value: value,
    };

    return this._bearerRequest(
      "post",
      "/global/variables/constants",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async modifyGlobalVariableConstant(name: string, value = null) {
    const payload = {
      Value: value,
    };

    return this._bearerRequest(
      "put",
      "/global/variables/constants/" + name,
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteGlobalVariableConstant(name = "") {
    return this._bearerRequest(
      "delete",
      "/global/variables/constants/" + name,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async createGlobalVariableList(
    name: string,
    apiName: string,
    desc = ""
  ) {
    const payload = {
      Name: name,
      APIName: apiName,
      Description: desc,
    };

    return this._bearerRequest(
      "post",
      "/global/variables/lists",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteGlobalVariableList(apiName: string) {
    return this._bearerRequest(
      "delete",
      "/global/variables/lists/" + apiName,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async addGlobalVariableListValue(
    listAPIName: string,
    payload: any = null
  ) {
    return this._bearerRequest(
      "post",
      "/global/variables/lists/" + listAPIName + "/values",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async addGlobalVariableListValues(
    listAPIName: string,
    payload: any = null
  ) {
    return this._bearerRequest(
      "post",
      "/global/variables/lists/" + listAPIName + "/values/multiple",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async modifyGlobalVariableListValue(
    listAPIName: string,
    variableID: string,
    payload: any = null
  ) {
    return this._bearerRequest(
      "put",
      "/global/variables/lists/" + listAPIName + "/values/" + variableID,
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteGlobalVariableListValue(
    listAPIName: string,
    variableID: string
  ) {
    return this._bearerRequest(
      "delete",
      "/global/variables/lists/" + listAPIName + "/values/" + variableID,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getAllCloudTenants() {
    return this._bearerRequest(
      "get",
      "/tenants",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getCloudTenantByID(id: string) {
    return this._bearerRequest(
      "get",
      "/tenants/" + id,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async createCloudTenant(payload: any = null) {
    return this._bearerRequest(
      "post",
      "/tenants",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteCloudTenant(cloudID: string) {
    return this._bearerRequest(
      "delete",
      "/tenants/" + cloudID,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteCloudPermanent(cloudID: string) {
    return this._bearerRequest(
      "delete",
      "/tenants/" + cloudID + "/permanent",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getDefaultPermissions() {
    return this._bearerRequest(
      "get",
      "/defaultPermissions",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getAllUserCloudTenants() {
    return this._bearerRequest(
      "get",
      "/account/tenants",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async favoriteCloud(payload: any) {
    return this._bearerRequest(
      "put",
      "/account/tenants/favorite",
      { payload },
      Cookies.get("authenticationToken")
    );
  }

  static async getAllPeople() {
    return this._bearerRequest(
      "get",
      "/people",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getPersonByID(id: string) {
    return this._bearerRequest(
      "get",
      "/people/" + id,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getUserRecordActivity(userID: string) {
    return this._bearerRequest(
      "get",
      `/people/${userID}/record-activity`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static async updateUserRecordActivity(payload: UserRecordActivity) {
    return this._bearerRequest(
      "put",
      `/people/record-activity`,
      { payload },
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static async addPersonToTenant(personID: string, tenantID: string) {
    return this._bearerRequest(
      "post",
      "/people/" + personID + "/tenants",
      {
        TenantID: tenantID,
      },
      Cookies.get("authenticationToken")
    );
  }

  static async deletePerson(personID: string) {
    return this._bearerRequest(
      "delete",
      "/people/" + personID,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async impersonateUser(userID: string) {
    const finalToken = Cookies.get("authenticationToken") || "undefined";

    return axios({
      method: "POST",
      url: `${window.GC_API_URL}/people/${userID}/impersonate`,
      headers: {
        Authorization: "Bearer " + finalToken,
        "app-id": "GrowthCloud",
      },
      data: null,
      withCredentials: true,
    });
  }

  static async enableDisableAccount(userID: string) {
    return this._bearerRequest(
      "put",
      `/people/${userID}/account/enable-disable`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getPersonCredentials(personID: string) {
    return this._bearerRequest(
      "get",
      "/people/" + personID + "/credentials",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async addSupportLoginPermissions(personID: string) {
    return this._bearerRequest(
      "post",
      "/people/" + personID + "/support-login-permissions",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async deleteSupportLoginPermissions(personID: string) {
    return this._bearerRequest(
      "delete",
      "/people/" + personID + "/support-login-permissions",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async updateCloud(tenantID: string, payload: any = null) {
    return this._bearerRequest(
      "put",
      "/tenants/" + tenantID,
      { payload },
      Cookies.get("authenticationToken")
    );
  }

  static async updateClouds(payload: any = null) {
    return this._bearerRequest(
      "put",
      "/account/updateTenants",
      { payload },
      Cookies.get("authenticationToken")
    );
  }

  static async removePersonFromTenant(tenantID: string, personID: string) {
    return this._bearerRequest(
      "delete",
      `/tenants/${tenantID}/people/${personID}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async removeTenantFromPerson(personID: string, tenantID: string) {
    return this._bearerRequest(
      "delete",
      `/people/${personID}/tenants/${tenantID}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async setMFA(enabled: boolean) {
    return this._bearerRequest(
      "put",
      `/account/security/mfa?enabled=${enabled}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getQuickTranscript(payload: any = null) {
    return this._bearerRequest(
      "post",
      "/tools/transcription",
      payload,
      Cookies.get("authenticationToken")
    );
  }

  static async searchChatGPT(searchText: string) {
    return this._bearerRequest(
      "post",
      "/tools/searchchatgpt",
      { Text: searchText },
      Cookies.get("authenticationToken")
    );
  }

  // ---------------------- Feedback ------------------------
  static async getFeedbackConversations() {
    return this._bearerRequest(
      "get",
      "/feedback/conversations",
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async getFeedbackConversation(id: string) {
    return this._bearerRequest(
      "get",
      `/feedback/conversations/${id}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async sendFeedback(message: FeedbackMessage) {
    return this._bearerRequest(
      "post",
      "/feedback",
      { message },
      Cookies.get("authenticationToken")
    );
  }

  static async processFeedbackDescription(conversationID: string) {
    return this._bearerRequest(
      "post",
      `/feedback/chat-description?feedbackID=${conversationID}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async searchFeedback(Query: any) {
    return this._bearerRequest(
      "put",
      "/feedback/search",
      { Query },
      Cookies.get("authenticationToken")
    );
  }

  static async updateFeedback(id: string, payload: any = null) {
    return this._bearerRequest(
      "put",
      `/feedback/${id}`,
      { payload },
      Cookies.get("authenticationToken")
    );
  }

  static async deleteFeedback(id: string) {
    return this._bearerRequest(
      "delete",
      `/feedback/${id}`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  // --------------------------------- Tenant Schemas -------------------------------------

  static async deleteTenantSchema(tenantID: string, tenantSchemaID: string) {
    return API._bearerRequest(
      "delete",
      `/tenants/${tenantID}/tenantSchema/${tenantSchemaID}/delete`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async cleanTenantSchemas(
    tenantID: string,
    tenantSchemasQuantity: number
  ) {
    return API._bearerRequest(
      "post",
      `/tenants/${tenantID}/tenantSchema/clean`,
      { tenantSchemasQuantity },
      Cookies.get("authenticationToken")
    );
  }

  static async exportTenantSchema(tenantID: string, tenantSchemaID: string) {
    return API._bearerRequest(
      "put",
      `/tenants/${tenantID}/tenantSchema/${tenantSchemaID}/export`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  // --------------------------------- Cloud Backups -------------------------------------
  static async deleteCloudBackup(tenantID: string, cloudBackupID: string) {
    return API._bearerRequest(
      "delete",
      `/tenants/${tenantID}/cloudBackup/${cloudBackupID}/delete`,
      null,
      Cookies.get("authenticationToken")
    );
  }

  static async cleanCloudBackups(
    supportTargetCloudID: string,
    cloudBackupsQuantity: number
  ) {
    return API._bearerRequest(
      "post",
      `/tenants/${supportTargetCloudID}/cloudBackup/clean`,
      { cloudBackupsQuantity },
      Cookies.get("authenticationToken")
    );
  }
}
